// Bouton flottant animé « Demander à Aurélia » : l'assistant reste à un clic depuis toutes les pages de l'application
// (dès la première page), sauf sur la page de l'assistant elle-même, les pages publiques ou de connexion et
// l'administration (boutons fixes au même endroit). En bas à droite, comme les lanceurs de chat habituels ; il passe
// sous les fenêtres et les notifications (z-index entre la barre d'en-tête et les modales).
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ButtonBase, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { AUTOPILOT_TOKEN_KEY } from '../../../services/autopilotAuthApi';
import { VAR_ADMIN, VAR_LOGIN } from '../../../constants/textVariable';
import { AUTONOMOUS_ROUTES, isAutonomousModeEnabled } from '../config';
import { useNavigateTo } from '../hooks/useNavigateTo';
import { AssistantApi, assistantApi } from '../services/assistantApi';
import { FONT, tint, useSoftTheme } from '../../shared/ui';

// « / » : page d'aiguillage (connexion, ou redirection vers l'application) : jamais de bouton
const HIDDEN_ON = ['/', AUTONOMOUS_ROUTES.assistant, `/${VAR_LOGIN}`, '/code', '/linkedin', '/welcoming', '/error', '/cgu',
                   '/confidentialite', '/subscription', `/${VAR_ADMIN}`];

const halo = keyframes`
    0% { transform: scale(1); opacity: 0.55; }
    70% { transform: scale(1.65); opacity: 0; }
    100% { transform: scale(1.65); opacity: 0; }
`;
const sparkle = keyframes`
    0%, 82%, 100% { transform: rotate(0deg) scale(1); }
    88% { transform: rotate(-14deg) scale(1.18); }
    94% { transform: rotate(10deg) scale(1.1); }
`;

/** Session valide : un jeton présent ET non expiré (un ancien jeton reste dans le navigateur après la fin de session :
 *  le bouton menait alors à l'assistant, qui renvoyait aussitôt à la page de connexion) */
const hasSession = (): boolean => {
    try {
        const token = localStorage.getItem(AUTOPILOT_TOKEN_KEY);
        if (!token) return false;
        const part = token.split('.')[1];
        if (!part) return true;                       // jeton sans date lisible : l'API tranchera
        const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof claims.exp !== 'number' || claims.exp * 1000 > Date.now();
    } catch {
        return false;
    }
};

const UNREAD_POLL_MS = 300_000;

const AssistantLauncher: React.FC<{ enabled?: boolean; api?: Pick<AssistantApi, 'list'> }> = ({ enabled = isAutonomousModeEnabled(), api = assistantApi }) => {
    const { pathname } = useLocation();
    const navigateTo = useNavigateTo();
    const theme = useSoftTheme();
    const [unread, setUnread] = useState(false);
    const hidden = !enabled || !hasSession() || HIDDEN_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`));
    // Résumé de la veille d'Aurélia pas encore lu : pastille sur le bouton
    useEffect(() => {
        if (hidden) return undefined;
        let alive = true;
        const read = () => api.list().then((l) => { if (alive) setUnread(l.some((c) => c.unread)); }).catch(() => undefined);
        read();
        const id = window.setInterval(read, UNREAD_POLL_MS);
        return () => { alive = false; window.clearInterval(id); };
    }, [hidden, api, pathname]);
    if (hidden) return null;
    return (
        <ButtonBase onClick={() => navigateTo(AUTONOMOUS_ROUTES.assistant)} aria-label="Demander à Aurélia"
                    data-testid="assistant-launcher"
                    sx={{
                        position: 'fixed', right: { xs: 16, sm: 24 }, bottom: { xs: 16, sm: 24 }, zIndex: 1250,
                        height: 56, minWidth: 56, px: 2, borderRadius: '28px', gap: 1, color: '#fff',
                        backgroundColor: theme,
                        boxShadow: `0 6px 20px ${tint(theme, 0.45)}`,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&::before': {
                            content: '""', position: 'absolute', inset: 0, borderRadius: 'inherit', zIndex: -1,
                            backgroundColor: theme, animation: `${halo} 2.4s ease-out infinite`,
                        },
                        '& .launcher-icon': { animation: `${sparkle} 4s ease-in-out infinite` },
                        '&:hover, &:focus-visible': { transform: 'translateY(-2px)', boxShadow: `0 10px 26px ${tint(theme, 0.55)}` },
                        '&:hover .launcher-label, &:focus-visible .launcher-label': { maxWidth: 200, opacity: 1, ml: 0.5 },
                        '@media (prefers-reduced-motion: reduce)': {
                            '&::before, & .launcher-icon': { animation: 'none' },
                        },
                    }}>
            <AutoAwesomeRoundedIcon className="launcher-icon" sx={{ fontSize: 26 }} />
            {unread && <span data-testid="assistant-launcher-unread" aria-label="Nouveau message d'Aurélia"
                             style={{ position: 'absolute', top: 6, right: 6, width: 12, height: 12, borderRadius: '50%',
                                      backgroundColor: '#C2410C', border: '2px solid #fff' }} />}
            <Typography className="launcher-label" component="span"
                        sx={{ fontFamily: FONT, fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden',
                              maxWidth: { xs: 0, sm: 200 }, opacity: { xs: 0, sm: 1 }, transition: 'max-width 0.25s ease, opacity 0.25s ease' }}>
                Demander à Aurélia
            </Typography>
        </ButtonBase>
    );
};

export default AssistantLauncher;
